import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import {
  authenticationManager,
  authExecutor,
  configurationManager,
  featuresManager,
  i18NManager,
} from "../../../../shared/core/service/services";
import { useObservable } from "../../../../shared/utils/observable";
import { Path } from "../../../core/routing/path";
import { changeLanguage } from "../../../domain/language/language";
import { useRTL } from "../../../domain/language/use-rtl";
import { getLanguageName } from "../../../utils/translations";
import { BootSplash } from "../../common/boot-splash";
import { BrandPanel } from "../../common/brand-panel";
import { BluePrimaryButton, WhitePrimaryButton } from "../../common/buttons/primary-button";
import { ExternalSites } from "../../common/external-sites-view";
import { SelectInput } from "../../common/forms/select-input";
import { StoreButtons } from "../../common/store-buttons-view";
import { GlobeIcon } from "../../common/svg/globe-icon";
import { theme } from "../../styles/theme";
import { UIConstants } from "../../styles/uiConstants";

const WarningIcon = require("../../../assets/images/svg/warning.svg");

export default function LoginScreen() {
  const { formatMessage } = useIntl();
  const { isRTL } = useRTL();
  const [ready, setReady] = useState(false);
  const features = useObservable(featuresManager.features);
  const location = useLocation();
  const hasBeenDisconnectedDueToInactivity = useObservable(authenticationManager.hasBeenDisconnectedDueToInactivity);
  const userLanguage = useObservable(i18NManager.localeTag);
  const configurationLanguages = useObservable(configurationManager.configuration).langs;
  const enrollmentEnabled = useObservable(configurationManager.configuration).enrollment;

  const history = useHistory();

  const goToRegisterPage = () => {
    history.push(features.sdaCustomerOnboardingCreate ? Path.Onboarding : Path.Register);
  };

  // The credentials form lives on the authorization server, never here: connecting hands the
  // browser over to it and comes back through the OAuth redirect.
  const connect = () => {
    history.push(Path.Root);
    authExecutor.connect();
  };

  useEffect(() => {
    if (authExecutor.withPhoneNumberAndPinCode()) {
      history.push(`${Path.Connect}${location.search ? location.search : ""}`);
    } else {
      setReady(true);
    }
  }, []);

  if (!ready) {
    return <BootSplash />;
  }

  return (
    <Page $isRTL={isRTL}>
      <BrandPanel>
        <BrandFooter>
          <ExternalSites />
          <StoreButtons />
        </BrandFooter>
      </BrandPanel>
      <FormPanel>
        <LanguageBar>
          <GlobeIcon width={14} height={14} />
          <LanguageLabel>{formatMessage("authScreen.languages")}</LanguageLabel>
          <SelectInput
            style={{ minWidth: "110px" }}
            value={userLanguage}
            innerId="language"
            options={configurationLanguages}
            itemRenderer={getLanguageName}
            disabledArrow
            withPointer
            onChange={changeLanguage}
          />
        </LanguageBar>
        <FormCard>
          {hasBeenDisconnectedDueToInactivity && (
            <StyledAlert>
              <WarningIconImage src={WarningIcon} alt="" />
              {formatMessage("authScreen.disconnectedDueToInactivity")}
            </StyledAlert>
          )}
          <WelcomeTitle>{formatMessage("authScreen.welcome")}</WelcomeTitle>
          <WelcomeSubtitle>{formatMessage("authScreen.subtitle")}</WelcomeSubtitle>
          <Actions>
            <LoginButton onClick={connect} size="S">
              {formatMessage("authScreen.login")}
            </LoginButton>
            {enrollmentEnabled && (
              <CreateAccountButton onClick={goToRegisterPage} size="S">
                {formatMessage("authScreen.createAccount")}
              </CreateAccountButton>
            )}
          </Actions>
        </FormCard>
      </FormPanel>
    </Page>
  );
}

const Page = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$isRTL ? "row-reverse" : "row")};
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;

  @media (max-width: ${UIConstants.TABLET_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const BrandFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;

  &:empty {
    display: none;
  }
`;

const FormPanel = styled.section`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 24px 32px 48px;
  background-color: #fbfbfb;
`;

const LanguageBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  align-self: flex-end;
  color: ${theme.colors.gray[700]};
`;

const LanguageLabel = styled.span`
  font-size: 0.8125rem;
`;

const FormCard = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const WelcomeTitle = styled.h2`
  ${theme.boldText};
  margin: 0 0 8px;
  font-size: 32px;
  line-height: normal;
  text-align: center;
  color: ${theme.colors.blue[800]};
`;

const WelcomeSubtitle = styled.p`
  ${theme.text};
  margin: 0 0 32px;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: ${theme.colors.gray[800]};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
`;

const LoginButton = styled(BluePrimaryButton)`
  width: 100%;
`;

/* Outlined, not a plain white fill: DefaultButton clears the border, so it is set back here. */
const CreateAccountButton = styled(WhitePrimaryButton)`
  width: 100%;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};

  &:focus,
  &:active,
  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;

const StyledAlert = styled.div`
  ${theme.bodyGreyRegular};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 10px;
  border-radius: 10px;
  background-color: ${theme.colors.warning[300]};
  color: ${theme.colors.warning[500]};
  font-size: 0.875rem;
  text-align: center;
`;

const WarningIconImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;
